import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { refetchUntilSettled } from '@/lib/graphql/settle-index-refetch';
import { isLovablePreview } from '@/lib/utils/environment';

/**
 * Delays before each follow-up refetch of the note lists, in ms (~19s in total).
 *
 * Every `getNotes` list (the Notes grid and the Deal/Company/Person Notes tabs) reads
 * the search index, which Furgus fills from the Mongo change stream after the write.
 * Measured on prd: ~1.8s median, 19s worst case. The single refetch AddNoteDialog used
 * to fire landed before the note did, cached an empty result, and the note only showed
 * after a page reload, so users re-saved it. Longer than the task schedule because
 * this runs in the background instead of holding a dialog open.
 */
export const NOTE_INDEX_RETRY_DELAYS_MS = [400, 900, 1_800, 3_000, 5_000, 8_000];

/**
 * Returns a fire-and-forget function that refetches the mounted note lists until the
 * newly created note comes back from the index, so it appears without a page reload.
 *
 * Stops as soon as any mounted `getNotes` list returns the note. A list whose filter the
 * note doesn't match (another deal's tab, a searched grid) never will, so with only such
 * lists mounted it retries to the end of the schedule and then gives up quietly.
 */
export function useSettleCreatedNote() {
  const client = useApolloClient();

  return useCallback(
    (noteId: string | null | undefined): void => {
      if (isLovablePreview() || !noteId) return;
      // Nothing on screen lists notes (e.g. "Add note" from a grid row with no sheet
      // open): every list fetches fresh when it mounts. Checking first also avoids
      // Apollo's warning for refetching a query name with no active observer.
      const hasNoteList = [...client.getObservableQueries('active').values()]
        .some(query => query.queryName === 'getNotes');
      if (!hasNoteList) return;

      void refetchUntilSettled(client, {
        firstInclude: ['getNotes'],
        retryInclude: ['getNotes'],
        delaysMs: NOTE_INDEX_RETRY_DELAYS_MS,
        isSettled: ids => ids.has(noteId),
      });
    },
    [client],
  );
}
